import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const createAuthHeader = (token) => ({
  Authorization: `Bearer ${token}`,
});

export const api = {
  // Auth
  login: async (email, password) => {
    const response = await axios.post(`${API_BASE}/auth/login`, { email, password });
    return response.data;
  },

  register: async (email, password, full_name, role = "analyst") => {
    const response = await axios.post(`${API_BASE}/auth/register`, {
      email,
      password,
      full_name,
      role,
    });
    return response.data;
  },

  getMe: async (token) => {
    const response = await axios.get(`${API_BASE}/auth/me`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  updateMyProfile: async (token, profileData) => {
    const response = await axios.put(`${API_BASE}/users/me/profile`, profileData, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  // Users
  getUsers: async (token, includeInactive = false) => {
    const params = includeInactive ? "?include_inactive=true" : "";
    const response = await axios.get(`${API_BASE}/users${params}`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  getPendingUsers: async (token) => {
    const response = await axios.get(`${API_BASE}/users/pending`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  approveUserById: async (token, userId) => {
    const response = await axios.post(`${API_BASE}/users/${userId}/approve`, {}, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  approveUserByToken: async (approvalToken) => {
    const response = await axios.get(`${API_BASE}/auth/approve?token=${approvalToken}`);
    return response.data;
  },

  rejectUser: async (token, userId, reason = null) => {
    const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    const response = await axios.post(`${API_BASE}/users/${userId}/reject${params}`, {}, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  updateUserRole: async (token, userId, role) => {
    const response = await axios.put(`${API_BASE}/users/${userId}/role?role=${role}`, {}, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  toggleUserActive: async (token, userId, isActive) => {
    const response = await axios.put(`${API_BASE}/users/${userId}/active?is_active=${isActive}`, {}, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  syncAllFromAirtable: async (token) => {
    const response = await axios.post(`${API_BASE}/sync/airtable`, {}, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  // Initiatives
  getInitiatives: async (token, filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await axios.get(`${API_BASE}/initiatives?${params}`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  getInitiativeFilters: async (token) => {
    const response = await axios.get(`${API_BASE}/initiatives/filters`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  getInitiative: async (token, id) => {
    const response = await axios.get(`${API_BASE}/initiatives/${id}`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  createInitiative: async (token, data) => {
    const response = await axios.post(`${API_BASE}/initiatives`, data, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  updateInitiative: async (token, id, data) => {
    const response = await axios.put(`${API_BASE}/initiatives/${id}`, data, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  deleteInitiative: async (token, id) => {
    const response = await axios.delete(`${API_BASE}/initiatives/${id}`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  // Pilots
  getPilots: async (token) => {
    const response = await axios.get(`${API_BASE}/pilots`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  createPilot: async (token, data) => {
    const response = await axios.post(`${API_BASE}/pilots`, data, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  updatePilot: async (token, id, data) => {
    const response = await axios.put(`${API_BASE}/pilots/${id}`, data, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  setPilotDecision: async (token, id, decision, notes) => {
    const response = await axios.put(
      `${API_BASE}/pilots/${id}/decision?decision=${decision}&notes=${encodeURIComponent(notes || "")}`,
      {},
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  // Projects
  getProjects: async (token) => {
    const response = await axios.get(`${API_BASE}/projects`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  createProject: async (token, data) => {
    const response = await axios.post(`${API_BASE}/projects`, data, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  updateProject: async (token, id, data) => {
    const response = await axios.put(`${API_BASE}/projects/${id}`, data, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  // Grant Calls
  getGrantCalls: async (token) => {
    const response = await axios.get(`${API_BASE}/grant-calls`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  createGrantCall: async (token, data) => {
    const response = await axios.post(`${API_BASE}/grant-calls`, data, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  // Grants Radar
  getGrantsRadar: async (token, { source, include_dismissed } = {}) => {
    const params = {};
    if (source) params.source = source;
    if (include_dismissed) params.include_dismissed = true;
    const response = await axios.get(`${API_BASE}/grants/radar`, {
      headers: createAuthHeader(token),
      params,
    });
    return response.data;
  },
  getGrantsRadarStats: async (token) => {
    const response = await axios.get(`${API_BASE}/grants/radar/stats`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },
  getGrantsRadarWidget: async (token) => {
    const response = await axios.get(`${API_BASE}/grants/radar/inicio-widget`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },
  refreshGrantsRadar: async (token) => {
    const response = await axios.post(`${API_BASE}/grants/radar/refresh`, {}, {
      headers: createAuthHeader(token),
      timeout: 180000,
    });
    return response.data;
  },
  dismissGrantsRadar: async (token, radarId) => {
    const response = await axios.post(`${API_BASE}/grants/radar/${radarId}/dismiss`, {}, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },
  undismissGrantsRadar: async (token, radarId) => {
    const response = await axios.post(`${API_BASE}/grants/radar/${radarId}/undismiss`, {}, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },
  saveRadarAsGrant: async (token, radarId) => {
    const response = await axios.post(`${API_BASE}/grants/radar/${radarId}/save-as-grant`, {}, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },
  addRadarAssignment: async (token, radarId, payload) => {
    const response = await axios.post(`${API_BASE}/grants/radar/${radarId}/assignments`, payload, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },
  removeRadarAssignment: async (token, radarId, index) => {
    const response = await axios.delete(`${API_BASE}/grants/radar/${radarId}/assignments/${index}`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },
  addGrantCallAssignment: async (token, grantId, payload) => {
    const response = await axios.post(`${API_BASE}/grant-calls/${grantId}/assignments`, payload, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },
  removeGrantCallAssignment: async (token, grantId, index) => {
    const response = await axios.delete(`${API_BASE}/grant-calls/${grantId}/assignments/${index}`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },
  deleteGrantCall: async (token, grantId) => {
    const response = await axios.delete(`${API_BASE}/grant-calls/${grantId}`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },
  getPg07Projects: async (token) => {
    const response = await axios.get(`${API_BASE}/pg07/projects`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  // Partners
  getPartners: async (token) => {
    const response = await axios.get(`${API_BASE}/partners`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  getPartnersUnified: async (token, filters = {}) => {
    const { search = "", source = "", page = 1, pageSize = 30, agentType = "", country = "",
            specialty = "", origin = "", fitNegocio = "", pipeline = "", statusFilter = "",
            batch = "", eventLeads = "" } = filters;
    const join = (v) => (Array.isArray(v) ? v.join(",") : v);
    const response = await axios.get(`${API_BASE}/partners/unified`, {
      headers: createAuthHeader(token),
      params: {
        search: search || undefined, source: source || undefined, page, page_size: pageSize,
        agent_type: join(agentType) || undefined, country: join(country) || undefined,
        specialty: join(specialty) || undefined, origin: join(origin) || undefined,
        fit_negocio: join(fitNegocio) || undefined, pipeline: join(pipeline) || undefined,
        status_filter: join(statusFilter) || undefined,
        batch: join(batch) || undefined, event_leads: join(eventLeads) || undefined,
      },
    });
    return response.data;
  },

  getPartnerCatalogs: async (token) => {
    const response = await axios.get(`${API_BASE}/partners/catalogs`, { headers: createAuthHeader(token) });
    return response.data;
  },

  savePartnerUnified: async (token, { curatedId, ecosystemId, fields }) => {
    const response = await axios.put(`${API_BASE}/partners/unified/save`, {
      curated_id: curatedId, ecosystem_id: ecosystemId, fields,
    }, { headers: createAuthHeader(token) });
    return response.data;
  },

  importPartnersCsv: async (token, { mapping, rows }) => {
    const response = await axios.post(`${API_BASE}/partners/import-csv`, { mapping, rows }, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  migrateObservatorioReplace: async (token, { mapping, rows, confirm }) => {
    const response = await axios.post(`${API_BASE}/partners/migrate-observatorio-replace`, { mapping, rows, confirm }, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  getPartnerComments: async (token, { curatedId, ecosystemId }) => {
    const response = await axios.get(`${API_BASE}/partners/comments`, {
      headers: createAuthHeader(token), params: { curated_id: curatedId || undefined, ecosystem_id: ecosystemId || undefined },
    });
    return response.data;
  },

  createPartnerComment: async (token, { curatedId, ecosystemId, text, agentName }) => {
    const response = await axios.post(`${API_BASE}/partners/comments`, {
      curated_id: curatedId, ecosystem_id: ecosystemId, text, agent_name: agentName,
    }, { headers: createAuthHeader(token) });
    return response.data;
  },

  reactToPartnerComment: async (token, commentId, emoji) => {
    const response = await axios.post(`${API_BASE}/partners/comments/${commentId}/react`, { emoji }, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  exportMentionsExcel: async (token) => {
    const response = await axios.get(`${API_BASE}/alerts/mentions/export`, {
      headers: createAuthHeader(token), responseType: "arraybuffer",
    });
    return response.data;
  },

  uploadPartnerAttachment: async (token, partnerId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await axios.post(`${API_BASE}/partners/${partnerId}/attachments`, formData, {
      headers: { ...createAuthHeader(token), "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  deletePartnerAttachment: async (token, partnerId, docId) => {
    const response = await axios.delete(`${API_BASE}/partners/${partnerId}/attachments/${docId}`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  downloadPartnerAttachment: async (token, partnerId, docId) => {
    const response = await axios.get(`${API_BASE}/partners/${partnerId}/attachments/${docId}/download`, {
      headers: createAuthHeader(token), responseType: "arraybuffer",
    });
    return response;
  },

  downloadPartnerPdf: async (token, { curatedId, ecosystemId }) => {
    const response = await axios.get(`${API_BASE}/partners/unified/pdf`, {
      headers: createAuthHeader(token), responseType: "arraybuffer",
      params: { curated_id: curatedId || undefined, ecosystem_id: ecosystemId || undefined },
    });
    return response.data;
  },

  createPartner: async (token, data) => {
    const response = await axios.post(`${API_BASE}/partners`, data, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  updatePartner: async (token, id, data) => {
    const response = await axios.put(`${API_BASE}/partners/${id}`, data, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  deletePartner: async (token, id) => {
    const response = await axios.delete(`${API_BASE}/partners/${id}`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  // Timesheets
  getTimesheets: async (token, filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await axios.get(`${API_BASE}/timesheets?${params}`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  createTimesheet: async (token, data) => {
    const response = await axios.post(`${API_BASE}/timesheets`, data, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  approveTimesheet: async (token, id) => {
    const response = await axios.put(
      `${API_BASE}/timesheets/${id}/approve`,
      {},
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  // Budget
  getBudgetLines: async (token, projectId) => {
    const params = projectId ? `?project_id=${projectId}` : "";
    const response = await axios.get(`${API_BASE}/budget-lines${params}`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  createBudgetLine: async (token, data) => {
    const response = await axios.post(`${API_BASE}/budget-lines`, data, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  // Expenses
  getExpenses: async (token, projectId) => {
    const params = projectId ? `?project_id=${projectId}` : "";
    const response = await axios.get(`${API_BASE}/expenses${params}`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  createExpense: async (token, data) => {
    const response = await axios.post(`${API_BASE}/expenses`, data, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  // Deliverables
  getDeliverables: async (token, projectId) => {
    const params = projectId ? `?project_id=${projectId}` : "";
    const response = await axios.get(`${API_BASE}/deliverables${params}`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  createDeliverable: async (token, data) => {
    const response = await axios.post(`${API_BASE}/deliverables`, data, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  // KPIs
  getKPIs: async (token, initiativeId) => {
    const params = initiativeId ? `?initiative_id=${initiativeId}` : "";
    const response = await axios.get(`${API_BASE}/kpis${params}`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  createKPI: async (token, data) => {
    const response = await axios.post(`${API_BASE}/kpis`, data, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  deleteKPI: async (token, kpiId) => {
    const response = await axios.delete(`${API_BASE}/kpis/${kpiId}`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  // Heatmap
  getHeatmapData: async (token) => {
    const response = await axios.get(`${API_BASE}/heatmap/data`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  // Sync to Airtable
  syncToAirtable: async (token, entityType, entityId, data, confirmSync = false) => {
    const response = await axios.post(`${API_BASE}/sync/to-airtable`, {
      entity_type: entityType,
      entity_id: entityId,
      data: data,
      confirm_sync: confirmSync,
    }, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  // Viewer Permissions
  getViewerConfig: async (token, userId) => {
    const response = await axios.get(`${API_BASE}/viewer/config/${userId}`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  updateViewerConfig: async (token, userId, permissions) => {
    const response = await axios.put(`${API_BASE}/viewer/config/${userId}`, permissions, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  getMyPermissions: async (token) => {
    const response = await axios.get(`${API_BASE}/viewer/my-permissions`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  // Risks
  getRisks: async (token, initiativeId) => {
    const params = initiativeId ? `?initiative_id=${initiativeId}` : "";
    const response = await axios.get(`${API_BASE}/risks${params}`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  createRisk: async (token, data) => {
    const response = await axios.post(`${API_BASE}/risks`, data, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  // Proveedores (análisis de proveedores por iniciativa)
  getSuppliers: async (token, initiativeId) => {
    const response = await axios.get(`${API_BASE}/initiatives/${initiativeId}/suppliers`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  createSupplier: async (token, initiativeId, data) => {
    const response = await axios.post(`${API_BASE}/initiatives/${initiativeId}/suppliers`, data, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  updateSupplier: async (token, supplierId, data) => {
    const response = await axios.put(`${API_BASE}/initiative-suppliers/${supplierId}`, data, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  deleteSupplier: async (token, supplierId) => {
    const response = await axios.delete(`${API_BASE}/initiative-suppliers/${supplierId}`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  // Pipeline I+D (hitos leídos del Gantt de seguimiento)
  getRdPipeline: async (token) => {
    const response = await axios.get(`${API_BASE}/rd-pipeline`, { headers: createAuthHeader(token) });
    return response.data;
  },

  updateRdPipelineItem: async (token, itemId, data) => {
    const response = await axios.put(`${API_BASE}/rd-pipeline/${itemId}`, data, { headers: createAuthHeader(token) });
    return response.data;
  },

  // Alerts
  getAlerts: async (token, unreadOnly = false) => {
    const response = await axios.get(`${API_BASE}/alerts?unread_only=${unreadOnly}`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  createAlert: async (token, data) => {
    const response = await axios.post(`${API_BASE}/alerts`, data, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  markAlertRead: async (token, id) => {
    const response = await axios.put(
      `${API_BASE}/alerts/${id}/read`,
      {},
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  // Calendar
  getCalendarEvents: async (token, filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await axios.get(`${API_BASE}/calendar?${params}`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  createCalendarEvent: async (token, data) => {
    const response = await axios.post(`${API_BASE}/calendar`, data, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  approveVacation: async (token, id, approved) => {
    const response = await axios.put(
      `${API_BASE}/calendar/${id}/approve?approved=${approved}`,
      {},
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  // AI
  aiSearch: async (token, query, searchType = "grants") => {
    const response = await axios.post(
      `${API_BASE}/ai/search`,
      { query, search_type: searchType },
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  generateReport: async (token, reportType, initiativeId) => {
    const params = initiativeId ? `&initiative_id=${initiativeId}` : "";
    const response = await axios.post(
      `${API_BASE}/ai/generate-report?report_type=${reportType}${params}`,
      {},
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  // Excel Exports
  exportInitiativesExcel: async (token) => {
    const response = await axios.get(`${API_BASE}/export/initiatives`, {
      headers: createAuthHeader(token),
      responseType: 'blob',
    });
    return response.data;
  },

  exportPortfolioExcel: async (token) => {
    const response = await axios.get(`${API_BASE}/export/portfolio`, {
      headers: createAuthHeader(token),
      responseType: 'blob',
    });
    return response.data;
  },

  exportGanttExcel: async (token) => {
    const response = await axios.get(`${API_BASE}/export/gantt`, {
      headers: createAuthHeader(token),
      responseType: 'blob',
    });
    return response.data;
  },

  exportFinancialsExcel: async (token) => {
    const response = await axios.get(`${API_BASE}/export/rd-economics`, {
      headers: createAuthHeader(token),
      responseType: 'blob',
    });
    return response.data;
  },

  // Teams Notifications
  testTeamsNotification: async (token) => {
    const response = await axios.post(`${API_BASE}/notifications/teams/test`, {}, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  sendTeamsMilestoneUpdate: async (token) => {
    const response = await axios.post(`${API_BASE}/notifications/teams/milestones`, {}, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  // Dashboard
  getDashboardStats: async (token) => {
    const response = await axios.get(`${API_BASE}/dashboard/stats`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  getFunnelData: async (token) => {
    const response = await axios.get(`${API_BASE}/dashboard/funnel`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  getPortfolioTrend: async (token) => {
    const response = await axios.get(`${API_BASE}/dashboard/portfolio-trend`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  getPortfolioTrendDetail: async (token, monthKey) => {
    const response = await axios.get(`${API_BASE}/dashboard/portfolio-trend/${monthKey}`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  exportBudgetExcel: async (token, year) => {
    const response = await axios.get(`${API_BASE}/budget/export/excel?year=${year}`, {
      headers: createAuthHeader(token),
      responseType: 'blob',
    });
    return response.data;
  },

  // Global Search
  globalSearch: async (token, query) => {
    const response = await axios.get(`${API_BASE}/search?q=${encodeURIComponent(query)}`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  // Seed Demo Data
  seedDemoData: async () => {
    const response = await axios.post(`${API_BASE}/seed-demo-data`);
    return response.data;
  },

  // Email
  sendEmail: async (token, recipientEmail, recipientName, subject, message) => {
    const response = await axios.post(
      `${API_BASE}/email/send`,
      { recipient_email: recipientEmail, recipient_name: recipientName, subject, message },
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  notifyTeam: async (token, subject, message) => {
    const response = await axios.post(
      `${API_BASE}/email/notify-team?subject=${encodeURIComponent(subject)}&message=${encodeURIComponent(message)}`,
      {},
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  // Innovation Copilot
  copilotChat: async (token, question, contextType, initiativeId) => {
    const response = await axios.post(
      `${API_BASE}/copilot/chat`,
      { question, context_type: contextType, initiative_id: initiativeId },
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  // Airtable
  getAirtableBases: async (token) => {
    const response = await axios.get(`${API_BASE}/airtable/bases`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  getAirtableTables: async (token, baseId) => {
    const response = await axios.get(`${API_BASE}/airtable/tables/${baseId}`, {
      headers: createAuthHeader(token),
    });
    return response.data;
  },

  getAirtableRecords: async (token, baseId, tableName) => {
    const response = await axios.get(
      `${API_BASE}/airtable/records/${baseId}/${tableName}`,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  syncInitiativesToAirtable: async (token, baseId, tableName) => {
    const response = await axios.post(
      `${API_BASE}/airtable/sync/initiatives?base_id=${baseId}&table_name=${tableName}`,
      {},
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  importInitiativesFromAirtable: async (token, baseId, tableName) => {
    const response = await axios.post(
      `${API_BASE}/airtable/import/initiatives?base_id=${baseId}&table_name=${tableName}`,
      {},
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  // Innovation Budget
  getInnovationBudget: async (token, year, category) => {
    let url = `${API_BASE}/innovation-budget`;
    const params = [];
    if (year) params.push(`year=${year}`);
    if (category) params.push(`category=${category}`);
    if (params.length) url += `?${params.join('&')}`;
    const response = await axios.get(url, { headers: createAuthHeader(token) });
    return response.data;
  },

  getBudgetSummary: async (token, year = 2026) => {
    const response = await axios.get(
      `${API_BASE}/innovation-budget/summary?year=${year}`,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  importInnovationBudget: async (token) => {
    const response = await axios.post(
      `${API_BASE}/import/innovation-budget-2026`,
      {},
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  // R&D Economics
  getRDEconomics: async (token, status) => {
    let url = `${API_BASE}/rd-economics`;
    if (status) url += `?status=${status}`;
    const response = await axios.get(url, { headers: createAuthHeader(token) });
    return response.data;
  },

  getRDEconomicsSummary: async (token) => {
    const response = await axios.get(
      `${API_BASE}/rd-economics/summary`,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  importRDProjects: async (token) => {
    const response = await axios.post(
      `${API_BASE}/import/rd-projects`,
      {},
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  // Consolidated Finance
  getConsolidatedFinance: async (token, year = 2026) => {
    const response = await axios.get(
      `${API_BASE}/finance/consolidated?year=${year}`,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  // Budget & RD Update
  updateBudgetItem: async (token, itemId, data) => {
    const response = await axios.put(
      `${API_BASE}/innovation-budget/${itemId}`,
      data,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  deleteBudgetItem: async (token, itemId) => {
    const response = await axios.delete(
      `${API_BASE}/innovation-budget/${itemId}`,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  updateRDEconomics: async (token, projectId, data) => {
    const response = await axios.put(
      `${API_BASE}/rd-economics/${projectId}`,
      data,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  // Gantt / Timeline
  getGanttTasks: async (token, initiativeId, projectId, includeSeed) => {
    let url = `${API_BASE}/gantt/tasks`;
    const params = [];
    if (initiativeId) params.push(`initiative_id=${initiativeId}`);
    if (projectId) params.push(`project_id=${projectId}`);
    if (includeSeed) params.push(`include_seed=true`);
    if (params.length) url += `?${params.join('&')}`;
    const response = await axios.get(url, { headers: createAuthHeader(token) });
    return response.data;
  },

  createGanttTask: async (token, data) => {
    const response = await axios.post(
      `${API_BASE}/gantt/tasks`,
      data,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  updateGanttTask: async (token, taskId, data) => {
    const response = await axios.put(
      `${API_BASE}/gantt/tasks/${taskId}`,
      data,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  deleteGanttTask: async (token, taskId) => {
    const response = await axios.delete(
      `${API_BASE}/gantt/tasks/${taskId}`,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  // Heatmap
  getPortfolioHeatmap: async (token) => {
    const response = await axios.get(
      `${API_BASE}/portfolio/heatmap`,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  updateInitiativeScores: async (token, initiativeId, impactScore, effortScore) => {
    const response = await axios.put(
      `${API_BASE}/initiatives/${initiativeId}/scores?impact_score=${impactScore}&effort_score=${effortScore}`,
      {},
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  // Gamification
  getBadgeDefinitions: async (token) => {
    const response = await axios.get(
      `${API_BASE}/gamification/badges`,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  getUserBadges: async (token, userId) => {
    const response = await axios.get(
      `${API_BASE}/gamification/user/${userId}/badges`,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  awardBadge: async (token, userId, badgeType, details) => {
    const response = await axios.post(
      `${API_BASE}/gamification/award-badge?user_id=${userId}&badge_type=${badgeType}${details ? `&details=${details}` : ''}`,
      {},
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  getLeaderboard: async (token) => {
    const response = await axios.get(
      `${API_BASE}/gamification/leaderboard`,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  checkAndAwardBadges: async (token, userId) => {
    const response = await axios.post(
      `${API_BASE}/gamification/check-badges/${userId}`,
      {},
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  // Airtable Full Sync
  syncAllFromAirtable: async (token) => {
    const response = await axios.post(
      `${API_BASE}/airtable/sync-all`,
      {},
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  // Executive Dashboard
  getExecutiveDashboard: async (token) => {
    const response = await axios.get(
      `${API_BASE}/dashboard/executive`,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  // Export to Excel
  exportInitiatives: (token) => {
    window.open(`${API_BASE}/export/initiatives?token=${token}`, '_blank');
  },

  exportRDEconomics: (token) => {
    window.open(`${API_BASE}/export/rd-economics?token=${token}`, '_blank');
  },

  exportBudget: (token, year = 2026) => {
    window.open(`${API_BASE}/export/budget?year=${year}&token=${token}`, '_blank');
  },

  exportFullReport: (token) => {
    window.open(`${API_BASE}/export/full-report?token=${token}`, '_blank');
  },

  // Teams notifications
  testTeamsNotification: async (token) => {
    const response = await axios.post(
      `${API_BASE}/notifications/teams/test`,
      {},
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  notifyInitiativeUpdate: async (token, initiativeId, updateType) => {
    const response = await axios.post(
      `${API_BASE}/notifications/teams/initiative-update?initiative_id=${initiativeId}&update_type=${updateType}`,
      {},
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  // Monetization
  getMonetizationData: async (token) => {
    const response = await axios.get(
      `${API_BASE}/monetization/data`,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  updateMonetizationKPI: async (token, category, kpiName, value) => {
    const response = await axios.put(
      `${API_BASE}/monetization/kpi`,
      { category, kpi_name: kpiName, value },
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  // Global Search Enhanced
  globalSearchEnhanced: async (token, query, filters = {}) => {
    const params = new URLSearchParams({ q: query, ...filters });
    const response = await axios.get(
      `${API_BASE}/search/enhanced?${params}`,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  // Initiative merges (fusión canónica de duplicados)
  listInitiativeMerges: async (token) => {
    const response = await axios.get(`${API_BASE}/admin/initiative-merges`, { headers: createAuthHeader(token) });
    return response.data;
  },
  restoreInitiativeMerge: async (token, initiativeId) => {
    const response = await axios.post(`${API_BASE}/admin/initiative-merges/${initiativeId}/restore`, {}, { headers: createAuthHeader(token) });
    return response.data;
  },

  // Alerta de discrepancias entre pantallas (Inicio/Pilotos/Producción)
  getCoherenceCheck: async (token) => {
    const response = await axios.get(`${API_BASE}/admin/coherence-check`, { headers: createAuthHeader(token) });
    return response.data;
  },

  // Fichas de solicitud de subvención (p.ej. Colombia)
  getGrantApplications: async (token) => {
    const response = await axios.get(`${API_BASE}/grant-applications`, { headers: createAuthHeader(token) });
    return response.data;
  },
  getGrantApplication: async (token, id) => {
    const response = await axios.get(`${API_BASE}/grant-applications/${id}`, { headers: createAuthHeader(token) });
    return response.data;
  },
  createGrantApplication: async (token, data) => {
    const response = await axios.post(`${API_BASE}/grant-applications`, data, { headers: createAuthHeader(token) });
    return response.data;
  },
  updateGrantApplication: async (token, id, data) => {
    const response = await axios.put(`${API_BASE}/grant-applications/${id}`, data, { headers: createAuthHeader(token) });
    return response.data;
  },
  deleteGrantApplication: async (token, id) => {
    const response = await axios.delete(`${API_BASE}/grant-applications/${id}`, { headers: createAuthHeader(token) });
    return response.data;
  },

  getDealflowDeals: async (token) => {
    const response = await axios.get(`${API_BASE}/dealflow-deals`, { headers: createAuthHeader(token) });
    return response.data;
  },

  getPrograms: async (token) => {
    const response = await axios.get(`${API_BASE}/programs`, { headers: createAuthHeader(token) });
    return response.data;
  },
  createProgram: async (token, body) => {
    const response = await axios.post(`${API_BASE}/programs`, body, { headers: createAuthHeader(token) });
    return response.data;
  },
  updateProgram: async (token, id, body) => {
    const response = await axios.put(`${API_BASE}/programs/${id}`, body, { headers: createAuthHeader(token) });
    return response.data;
  },
  deleteProgram: async (token, id) => {
    const response = await axios.delete(`${API_BASE}/programs/${id}`, { headers: createAuthHeader(token) });
    return response.data;
  },

  getContacts: async (token) => {
    const response = await axios.get(`${API_BASE}/contacts`, { headers: createAuthHeader(token) });
    return response.data;
  },
  getInteractions: async (token) => {
    const response = await axios.get(`${API_BASE}/interactions`, { headers: createAuthHeader(token) });
    return response.data;
  },
  getClimateTechAccelerators: async (token) => {
    const response = await axios.get(`${API_BASE}/climatetech-accelerators`, { headers: createAuthHeader(token) });
    return response.data;
  },

  getEsgTagsCatalog: async (token) => {
    const response = await axios.get(`${API_BASE}/initiatives/esg-tags-catalog`, { headers: createAuthHeader(token) });
    return response.data;
  },
  uploadInitiativeDocument: async (token, initiativeId, file) => {
    const form = new FormData();
    form.append("file", file);
    const response = await axios.post(`${API_BASE}/initiatives/${initiativeId}/documents`, form, { headers: createAuthHeader(token) });
    return response.data;
  },
  deleteInitiativeDocument: async (token, initiativeId, docIndex) => {
    const response = await axios.delete(`${API_BASE}/initiatives/${initiativeId}/documents/${docIndex}`, { headers: createAuthHeader(token) });
    return response.data;
  },

  getDuplicateCandidates: async (token) => {
    const response = await axios.get(`${API_BASE}/cross-link/duplicate-candidates`, { headers: createAuthHeader(token) });
    return response.data;
  },
  mergeDuplicate: async (token, body) => {
    const response = await axios.post(`${API_BASE}/cross-link/merge-duplicate`, body, { headers: createAuthHeader(token) });
    return response.data;
  },

  // Scheduled Reports
  getScheduledReports: async (token) => {
    const response = await axios.get(
      `${API_BASE}/reports/scheduled`,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  createScheduledReport: async (token, reportConfig) => {
    const response = await axios.post(
      `${API_BASE}/reports/scheduled`,
      reportConfig,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  sendReportNow: async (token, reportType, recipients) => {
    const response = await axios.post(
      `${API_BASE}/reports/send-now`,
      { report_type: reportType, recipients },
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  // ESG Module
  getESGData: async (token) => {
    const response = await axios.get(
      `${API_BASE}/esg/data`,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  updateESGKPI: async (token, kpiId, value, year) => {
    const response = await axios.put(
      `${API_BASE}/esg/kpi`,
      { kpi_id: kpiId, value, year },
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  exportESGToExcel: async (token) => {
    const response = await axios.get(
      `${API_BASE}/esg/export/excel`,
      { headers: createAuthHeader(token), responseType: 'arraybuffer' }
    );
    return response.data;
  },

  importESGData: async (token, data) => {
    const response = await axios.post(
      `${API_BASE}/esg/import`,
      { data },
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  // Timesheets Enhanced
  getTimesheetTemplates: async (token) => {
    const response = await axios.get(
      `${API_BASE}/timesheets/templates`,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  sendTimesheetForSignature: async (token, data) => {
    const response = await axios.post(
      `${API_BASE}/timesheets/send-signature`,
      data,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  getTimesheetSignatures: async (token) => {
    const response = await axios.get(
      `${API_BASE}/timesheets/signatures`,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  managerSignTimesheet: async (token, signatureId, data) => {
    const response = await axios.post(
      `${API_BASE}/timesheets/manager-sign/${signatureId}`,
      data,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  // Deliverables
  getProjectDeliverables: async (token, projectId) => {
    const response = await axios.get(
      `${API_BASE}/projects/${projectId}/deliverables`,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  createDeliverable: async (token, formData) => {
    const response = await axios.post(
      `${API_BASE}/deliverables/with-file`,
      formData,
      { headers: { ...createAuthHeader(token), 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  deleteDeliverable: async (token, deliverableId) => {
    const response = await axios.delete(
      `${API_BASE}/deliverables/${deliverableId}`,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  uploadDeliverableFile: async (token, deliverableId, formData) => {
    const response = await axios.post(
      `${API_BASE}/deliverables/${deliverableId}/upload`,
      formData,
      { headers: { ...createAuthHeader(token), 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  updateDeliverableStatus: async (token, deliverableId, status) => {
    const response = await axios.put(
      `${API_BASE}/deliverables/${deliverableId}/status`,
      { status },
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  // Settings - Integration Status
  getIntegrationStatus: async (token) => {
    const response = await axios.get(
      `${API_BASE}/settings/integrations/status`,
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  // Patents
  getPatents: async (token) => {
    const response = await axios.get(`${API_BASE}/patents`, { headers: createAuthHeader(token) });
    return response.data;
  },

  createPatent: async (token, data) => {
    const response = await axios.post(`${API_BASE}/patents`, data, { headers: createAuthHeader(token) });
    return response.data;
  },

  updatePatent: async (token, patentId, data) => {
    const response = await axios.put(`${API_BASE}/patents/${patentId}`, data, { headers: createAuthHeader(token) });
    return response.data;
  },

  deletePatent: async (token, patentId) => {
    const response = await axios.delete(`${API_BASE}/patents/${patentId}`, { headers: createAuthHeader(token) });
    return response.data;
  },

  getPatentPayments: async (token) => {
    const response = await axios.get(`${API_BASE}/patents/payments`, { headers: createAuthHeader(token) });
    return response.data;
  },

  createPatentPayment: async (token, data) => {
    const response = await axios.post(`${API_BASE}/patents/payments`, data, { headers: createAuthHeader(token) });
    return response.data;
  },

  deletePatentPayment: async (token, paymentId) => {
    const response = await axios.delete(`${API_BASE}/patents/payments/${paymentId}`, { headers: createAuthHeader(token) });
    return response.data;
  },

  markPatentPaymentPaid: async (token, paymentId) => {
    const response = await axios.put(`${API_BASE}/patents/payments/${paymentId}/paid`, {}, { headers: createAuthHeader(token) });
    return response.data;
  },

  // ESG EINF Export
  exportESGEINF: async (token) => {
    const response = await axios.get(
      `${API_BASE}/esg/export/einf`,
      { headers: createAuthHeader(token), responseType: 'arraybuffer' }
    );
    return response.data;
  },

  // ESG Documents
  getESGDocuments: async (token) => {
    const response = await axios.get(`${API_BASE}/esg/documents`, { headers: createAuthHeader(token) });
    return response.data;
  },

  addESGDocument: async (token, data) => {
    const response = await axios.post(`${API_BASE}/esg/documents`, data, { headers: createAuthHeader(token) });
    return response.data;
  },

  deleteESGDocument: async (token, documentId) => {
    const response = await axios.delete(`${API_BASE}/esg/documents/${documentId}`, { headers: createAuthHeader(token) });
    return response.data;
  },

  // Editable KPIs
  getEditableKPIs: async (token, category = null, year = null) => {
    const params = new URLSearchParams();
    if (category) params.append("category", category);
    if (year) params.append("year", year);
    const response = await axios.get(`${API_BASE}/kpis/editable?${params}`, { headers: createAuthHeader(token) });
    return response.data;
  },

  updateEditableKPI: async (token, kpiId, data) => {
    const response = await axios.put(`${API_BASE}/kpis/editable/${kpiId}`, data, { headers: createAuthHeader(token) });
    return response.data;
  },

  createEditableKPI: async (token, category, kpiName, value, unit = null, year = 2026, source = null) => {
    const params = new URLSearchParams({ category, kpi_name: kpiName, value, year });
    if (unit) params.append("unit", unit);
    if (source) params.append("source", source);
    const response = await axios.post(`${API_BASE}/kpis/editable?${params}`, {}, { headers: createAuthHeader(token) });
    return response.data;
  },

  // R&D Project Balances
  getRDProjectBalances: async (token, year = null, includeInvestmentAid = false) => {
    const params = new URLSearchParams();
    if (year) params.append("year", year);
    if (includeInvestmentAid) params.append("include_investment_aid", "true");
    const response = await axios.get(`${API_BASE}/rd-projects/balances?${params}`, { headers: createAuthHeader(token) });
    return response.data;
  },

  getRDBalancesSummary: async (token, year = 2026, includeInvestmentAid = false) => {
    const params = new URLSearchParams({ year });
    if (includeInvestmentAid) params.append("include_investment_aid", "true");
    const response = await axios.get(`${API_BASE}/rd-projects/balances/summary?${params}`, { headers: createAuthHeader(token) });
    return response.data;
  },

  // Estadísticas unificadas (Home/Dashboard/Pilotos/Balances leen todos de aquí)
  getUnifiedStats: async (token) => {
    const response = await axios.get(`${API_BASE}/stats`, { headers: createAuthHeader(token) });
    return response.data;
  },

  // Gobierno documental ISO
  getGovernanceDocs: async (token, category = null) => {
    const params = category ? `?category=${category}` : "";
    const response = await axios.get(`${API_BASE}/governance-docs${params}`, { headers: createAuthHeader(token) });
    return response.data;
  },

  uploadGovernanceDoc: async (token, docId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await axios.post(`${API_BASE}/governance-docs/${docId}/upload`, formData, {
      headers: { ...createAuthHeader(token), "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  createRDProjectBalance: async (token, data) => {
    const response = await axios.post(`${API_BASE}/rd-projects/balances`, data, { headers: createAuthHeader(token) });
    return response.data;
  },

  updateRDProjectBalance: async (token, balanceId, data) => {
    const params = new URLSearchParams(data);
    const response = await axios.put(`${API_BASE}/rd-projects/balances/${balanceId}?${params}`, {}, { headers: createAuthHeader(token) });
    return response.data;
  },

  deleteRDProjectBalance: async (token, balanceId) => {
    const response = await axios.delete(`${API_BASE}/rd-projects/balances/${balanceId}`, { headers: createAuthHeader(token) });
    return response.data;
  },

  getRdImportIncidents: async (token) => {
    const response = await axios.get(`${API_BASE}/rd-projects/import-incidents`, { headers: createAuthHeader(token) });
    return response.data;
  },

  getRdBalanceCorrections: async (token, projectName = null) => {
    const params = projectName ? `?project_name=${encodeURIComponent(projectName)}` : "";
    const response = await axios.get(`${API_BASE}/rd-projects/balances/corrections${params}`, { headers: createAuthHeader(token) });
    return response.data;
  },

  getRdAnnualBreakdown: async (token, projectName = null) => {
    const params = projectName ? `?project_name=${encodeURIComponent(projectName)}` : "";
    const response = await axios.get(`${API_BASE}/rd-projects/annual-breakdown${params}`, { headers: createAuthHeader(token) });
    return response.data;
  },

  getRdDeviationAlerts: async (token, projectName = null) => {
    const params = projectName ? `?project_name=${encodeURIComponent(projectName)}` : "";
    const response = await axios.get(`${API_BASE}/rd-projects/balances/deviation-alerts${params}`, { headers: createAuthHeader(token) });
    return response.data;
  },

  importRDBalancesFromExcel: async (token, file, year = 2026) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await axios.post(
      `${API_BASE}/rd-projects/balances/import?year=${year}`,
      formData,
      { headers: { ...createAuthHeader(token), "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  },

  // ESG PDF Export
  exportESGPDF: async (token) => {
    const response = await axios.get(
      `${API_BASE}/esg/export/pdf`,
      { headers: createAuthHeader(token), responseType: 'arraybuffer' }
    );
    return response.data;
  },

  // AI Reports
  generateAIReport: async (token, reportType = "monthly") => {
    const response = await axios.post(
      `${API_BASE}/reports/generate?report_type=${reportType}`,
      {},
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  scheduleAutomatedReports: async (token, frequency = "monthly") => {
    const response = await axios.post(
      `${API_BASE}/reports/schedule?frequency=${frequency}`,
      {},
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  // Digital Signatures
  requestTimesheetSignature: async (token, timesheetId, recipientEmail, recipientName) => {
    const response = await axios.post(
      `${API_BASE}/timesheets/request-signature?timesheet_id=${timesheetId}&recipient_email=${encodeURIComponent(recipientEmail)}&recipient_name=${encodeURIComponent(recipientName)}`,
      {},
      { headers: createAuthHeader(token) }
    );
    return response.data;
  },

  signTimesheet: async (signatureToken, signatureData) => {
    const response = await axios.post(
      `${API_BASE}/timesheets/sign/${signatureToken}`,
      signatureData
    );
    return response.data;
  },

  getSignatureStatus: async (signatureToken) => {
    const response = await axios.get(`${API_BASE}/timesheets/signature-status/${signatureToken}`);
    return response.data;
  },

  // Claves API de máquina (M2M) — ticket "API de solo lectura para Pando/SofIA" 2026-08-31
  listApiKeys: async (token) => {
    const response = await axios.get(`${API_BASE}/admin/api-keys`, { headers: createAuthHeader(token) });
    return response.data;
  },
  createApiKey: async (token, name, scopes = []) => {
    const response = await axios.post(`${API_BASE}/admin/api-keys`, { name, scopes }, { headers: createAuthHeader(token) });
    return response.data;
  },
  revokeApiKey: async (token, keyId) => {
    const response = await axios.post(`${API_BASE}/admin/api-keys/${keyId}/revoke`, {}, { headers: createAuthHeader(token) });
    return response.data;
  },
  deleteApiKey: async (token, keyId) => {
    const response = await axios.delete(`${API_BASE}/admin/api-keys/${keyId}`, { headers: createAuthHeader(token) });
    return response.data;
  },

  // Propuestas I+D — wizard de fichas de propuesta con branding corporativo (2026-09-08)
  getProposals: async (token, status) => {
    const params = status ? `?status=${status}` : "";
    const response = await axios.get(`${API_BASE}/proposals${params}`, { headers: createAuthHeader(token) });
    return response.data;
  },
  getProposalStats: async (token) => {
    const response = await axios.get(`${API_BASE}/proposals/stats`, { headers: createAuthHeader(token) });
    return response.data;
  },
  getProposal: async (token, id) => {
    const response = await axios.get(`${API_BASE}/proposals/${id}`, { headers: createAuthHeader(token) });
    return response.data;
  },
  createProposal: async (token, payload) => {
    const response = await axios.post(`${API_BASE}/proposals`, payload, { headers: createAuthHeader(token) });
    return response.data;
  },
  updateProposal: async (token, id, payload) => {
    const response = await axios.put(`${API_BASE}/proposals/${id}`, payload, { headers: createAuthHeader(token) });
    return response.data;
  },
  deleteProposal: async (token, id) => {
    const response = await axios.delete(`${API_BASE}/proposals/${id}`, { headers: createAuthHeader(token) });
    return response.data;
  },
  sendProposalForReview: async (token, id) => {
    const response = await axios.post(`${API_BASE}/proposals/${id}/send-for-review`, {}, { headers: createAuthHeader(token) });
    return response.data;
  },
  decideProposal: async (token, id, decision, notes) => {
    const response = await axios.post(`${API_BASE}/proposals/${id}/decision`, { decision, notes }, { headers: createAuthHeader(token) });
    return response.data;
  },
  resubmitProposal: async (token, id) => {
    const response = await axios.post(`${API_BASE}/proposals/${id}/resubmit`, {}, { headers: createAuthHeader(token) });
    return response.data;
  },
  searchConvocatoriaAI: async (token, query) => {
    const response = await axios.post(`${API_BASE}/proposals/search-convocatoria`, { query }, { headers: createAuthHeader(token) });
    return response.data;
  },
  aiDraftProposal: async (token, payload) => {
    const response = await axios.post(`${API_BASE}/proposals/ai-draft`, payload, { headers: createAuthHeader(token) });
    return response.data;
  },

  // Personal disponible en Valoriza — directorio + asignaciones (2026-09-10)
  getPersonalStats: async (token) => {
    const response = await axios.get(`${API_BASE}/personal/stats`, { headers: createAuthHeader(token) });
    return response.data;
  },
  getPersonalFiltros: async (token) => {
    const response = await axios.get(`${API_BASE}/personal/filtros`, { headers: createAuthHeader(token) });
    return response.data;
  },
  listPersonal: async (token, params = {}) => {
    const response = await axios.get(`${API_BASE}/personal`, { headers: createAuthHeader(token), params });
    return response.data;
  },
  revealPersonalDni: async (token, personId) => {
    const response = await axios.get(`${API_BASE}/personal/${personId}/dni`, { headers: createAuthHeader(token) });
    return response.data;
  },
  updatePersonal: async (token, personId, payload) => {
    const response = await axios.put(`${API_BASE}/personal/${personId}`, payload, { headers: createAuthHeader(token) });
    return response.data;
  },
  listPersonalAssignments: async (token, params = {}) => {
    const response = await axios.get(`${API_BASE}/personal/asignaciones`, { headers: createAuthHeader(token), params });
    return response.data;
  },
  getPersonalAssignmentsSummary: async (token, params = {}) => {
    const response = await axios.get(`${API_BASE}/personal/asignaciones/resumen`, { headers: createAuthHeader(token), params });
    return response.data;
  },
  createPersonalAssignment: async (token, personId, payload) => {
    const response = await axios.post(`${API_BASE}/personal/${personId}/asignaciones`, payload, { headers: createAuthHeader(token) });
    return response.data;
  },
  deletePersonalAssignment: async (token, assignmentId) => {
    const response = await axios.delete(`${API_BASE}/personal/asignaciones/${assignmentId}`, { headers: createAuthHeader(token) });
    return response.data;
  },

  // Tablero de tareas (personas + agentes) — spec "emergent-spec-tablero-tareas-agentes.md" 2026-10-25
  listTasks: async (token, params = {}) => {
    const response = await axios.get(`${API_BASE}/tasks`, { headers: createAuthHeader(token), params });
    return response.data;
  },
  getTask: async (token, taskId) => {
    const response = await axios.get(`${API_BASE}/tasks/${taskId}`, { headers: createAuthHeader(token) });
    return response.data;
  },
  createTask: async (token, payload) => {
    const response = await axios.post(`${API_BASE}/tasks`, payload, { headers: createAuthHeader(token) });
    return response.data;
  },
  updateTask: async (token, taskId, payload) => {
    const response = await axios.patch(`${API_BASE}/tasks/${taskId}`, payload, { headers: createAuthHeader(token) });
    return response.data;
  },
  moveTask: async (token, taskId, payload) => {
    const response = await axios.post(`${API_BASE}/tasks/${taskId}/move`, payload, { headers: createAuthHeader(token) });
    return response.data;
  },
  deleteTask: async (token, taskId) => {
    const response = await axios.delete(`${API_BASE}/tasks/${taskId}`, { headers: createAuthHeader(token) });
    return response.data;
  },
  getTaskComments: async (token, taskId) => {
    const response = await axios.get(`${API_BASE}/tasks/${taskId}/comments`, { headers: createAuthHeader(token) });
    return response.data;
  },
  addTaskComment: async (token, taskId, payload) => {
    const response = await axios.post(`${API_BASE}/tasks/${taskId}/comments`, payload, { headers: createAuthHeader(token) });
    return response.data;
  },
  getTaskEvents: async (token, taskId) => {
    const response = await axios.get(`${API_BASE}/tasks/${taskId}/events`, { headers: createAuthHeader(token) });
    return response.data;
  },
  getTaskCounts: async (token, params = {}) => {
    const response = await axios.get(`${API_BASE}/tasks/counts`, { headers: createAuthHeader(token), params });
    return response.data;
  },
  getMyTasks: async (token) => {
    const response = await axios.get(`${API_BASE}/tasks/mine`, { headers: createAuthHeader(token) });
    return response.data;
  },
};

export default api;
